import React, { useState, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { usePartners } from '../context/PartnersContext';
import './PageStyles.css';
import './Partners.css';

const COUNTRY_TO_CODE = { USA: 'us', India: 'in', UK: 'gb', UAE: 'ae', Singapore: 'sg', Other: 'eu' };
const getFlagUrl = (country) => `https://flagcdn.com/${COUNTRY_TO_CODE[country] || 'eu'}.svg`;

const generateId = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let id = '';
  for (let i = 0; i < 6; i++) id += chars.charAt(Math.floor(Math.random() * chars.length));
  return id;
};

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const mon = MONTHS[d.getMonth()];
  const year = d.getFullYear();
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12 || 12;
  return `${day} ${mon} ${year}, ${h}:${String(m).padStart(2, '0')} ${ampm}`;
};

const CopyIcon = () => (
  <span className="copy-icon" aria-hidden="true">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  </span>
);

const Partners = () => {
  const { partners, setPartners, addPartnerWithLogs, PARTNER_NAMES } = usePartners();
  const [configurePartner, setConfigurePartner] = useState(null);
  const [showCredentialsPopup, setShowCredentialsPopup] = useState(false);
  const [oauthStep, setOauthStep] = useState('authorize');
  const [showDelinkConfirm, setShowDelinkConfirm] = useState(false);
  const [credentials, setCredentials] = useState({ clientId: '', clientSecret: '', appId: '', apiKey: '' });
  const [showAddPartner, setShowAddPartner] = useState(false);
  const [addPartnerSelected, setAddPartnerSelected] = useState('');
  const nextId = partners.length ? Math.max(...partners.map((p) => p.id)) + 1 : 1;

  const availableToAdd = PARTNER_NAMES.filter((name) => !partners.some((p) => p.name === name));

  const copyToClipboard = useCallback((text) => {
    navigator.clipboard.writeText(text);
  }, []);

  const openConfigure = (partner) => {
    setConfigurePartner(partner);
    setOauthStep('authorize');
    setShowDelinkConfirm(false);
  };

  const closeConfigure = () => {
    setConfigurePartner(null);
    setShowCredentialsPopup(false);
    setShowDelinkConfirm(false);
  };

  const handleAddPartner = () => {
    if (!addPartnerSelected) return;
    const name = addPartnerSelected;
    addPartnerWithLogs({
      id: nextId,
      name,
      code: generateId(),
      apiKey: `${name.replace(/\s/g, '').toLowerCase().slice(0, 6)}***${String(1000 + nextId).slice(-4)}`,
      clientId: `cli_***${nextId}`,
      clientSecret: `sec_***${nextId}`,
      corridorFrom: 'USA',
      corridorTo: 'India',
      status: 'Approved',
      dateApproved: new Date().toISOString().slice(0, 19).replace('T', 'T'),
      dateActivated: null,
    });
    setAddPartnerSelected('');
    setShowAddPartner(false);
  };

  const handleAuthorize = () => {
    setShowCredentialsPopup(true);
  };

  const saveCredentials = () => {
    if (configurePartner) {
      const now = new Date().toISOString().slice(0, 19);
      setPartners((prev) =>
        prev.map((p) =>
          p.id === configurePartner.id
            ? { ...p, status: 'Active', dateActivated: now }
            : p
        )
      );
      setConfigurePartner((p) => (p ? { ...p, status: 'Active', dateActivated: now } : null));
    }
    setShowCredentialsPopup(false);
    setCredentials({ clientId: '', clientSecret: '', appId: '', apiKey: '' });
    setOauthStep('token-saved');
  };

  const handleDelink = () => {
    if (!configurePartner) return;
    setPartners((prev) =>
      prev.map((p) =>
        p.id === configurePartner.id ? { ...p, status: 'Approved', dateActivated: null } : p
      )
    );
    setShowDelinkConfirm(false);
    closeConfigure();
  };

  const CorridorWithFlags = ({ from, to, status }) => (
    <span className="corridor-with-flags">
      <img src={getFlagUrl(from)} alt={from} className="corridor-flag" />
      <span>{from}</span>
      <span className="corridor-arrow">→</span>
      <img src={getFlagUrl(to)} alt={to} className="corridor-flag" />
      <span>{to}</span>
      {status && <span className={`status-badge ${status.toLowerCase()} corridor-status`}>{status}</span>}
    </span>
  );

  return (
    <DashboardLayout>
      <div className="page-container">
        <div className="card">
          <div className="card-header">
            <h2>Partners</h2>
            <button type="button" className="btn btn-add-partner" onClick={() => setShowAddPartner(true)}>
              Add New Partner
            </button>
          </div>

          <table className="table partners-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Partner name</th>
                <th>API Key</th>
                <th>Corridor</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {partners.map((partner) => (
                <tr key={partner.id}>
                  <td>
                    <span className="partner-id-cell">
                      <code className="partner-code">{partner.code}</code>
                      <button
                        type="button"
                        className="btn-icon-copy"
                        onClick={() => copyToClipboard(partner.code)}
                        title="Copy ID"
                        aria-label="Copy ID"
                      >
                        <CopyIcon />
                      </button>
                    </span>
                  </td>
                  <td><strong>{partner.name}</strong></td>
                  <td><code className="api-key-cell">{partner.apiKey}</code></td>
                  <td>
                    <CorridorWithFlags from={partner.corridorFrom} to={partner.corridorTo} status={partner.status} />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-configure btn-sm"
                      onClick={() => openConfigure(partner)}
                    >
                      Configure
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Add New Partner popup */}
        {showAddPartner && (
          <div className="modal-overlay" onClick={() => setShowAddPartner(false)}>
            <div className="modal-content add-partner-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header modal-header-blue">
                <h3>Add New Partner</h3>
                <button type="button" className="modal-close modal-close-white" onClick={() => setShowAddPartner(false)} aria-label="Close">×</button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Select partner</label>
                  <select
                    className="form-input"
                    value={addPartnerSelected}
                    onChange={(e) => setAddPartnerSelected(e.target.value)}
                  >
                    <option value="">Choose a partner...</option>
                    {availableToAdd.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </div>
                {availableToAdd.length === 0 && <p className="form-hint">All partners are already active or approved for this user.</p>}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddPartner(false)}>Cancel</button>
                <button type="button" className="btn btn-add-partner" onClick={handleAddPartner} disabled={!addPartnerSelected}>Add Partner</button>
              </div>
            </div>
          </div>
        )}

        {/* Configure modal */}
        {configurePartner && (
          <div className="modal-overlay" onClick={closeConfigure}>
            <div className="modal-content configure-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header modal-header-blue">
                <h3>Configure ({configurePartner.name})</h3>
                <button type="button" className="modal-close modal-close-white" onClick={closeConfigure} aria-label="Close">×</button>
              </div>
              <div className="modal-body">
                {configurePartner.status === 'Approved' && (
                  <>
                    {oauthStep === 'authorize' && (
                      <div className="configure-approved">
                        <p className="configure-desc">Complete the OAuth 2.0 token flow: authorize with the partner, then save your app credentials (Client ID, Client Secret, API Key) in the popup to acquire and store the token.</p>
                        <button type="button" className="btn btn-add-partner" onClick={handleAuthorize}>
                          Authorize
                        </button>
                      </div>
                    )}
                    {oauthStep === 'token-saved' && (
                      <div className="oauth-token-saved">
                        <span className="success-icon">✓</span>
                        <p>Token is saved. Partner is now authorized.</p>
                      </div>
                    )}
                  </>
                )}
                {configurePartner.status === 'Active' && (
                  <div className="configure-active">
                    <table className="configure-table">
                      <tbody>
                        <tr>
                          <th>Name</th>
                          <td>{configurePartner.name}</td>
                        </tr>
                        <tr>
                          <th>Client ID</th>
                          <td>
                            <span className="api-key-with-copy">
                              <code>{configurePartner.clientId || '—'}</code>
                              <button type="button" className="btn-icon-copy" onClick={() => copyToClipboard(configurePartner.clientId)} title="Copy" aria-label="Copy"><CopyIcon /></button>
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <th>Client Secret</th>
                          <td>
                            <span className="api-key-with-copy">
                              <code>{configurePartner.clientSecret || '—'}</code>
                              <button type="button" className="btn-icon-copy" onClick={() => copyToClipboard(configurePartner.clientSecret)} title="Copy" aria-label="Copy"><CopyIcon /></button>
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <th>API Key</th>
                          <td>
                            <span className="api-key-with-copy">
                              <code>{configurePartner.apiKey}</code>
                              <button type="button" className="btn-icon-copy" onClick={() => copyToClipboard(configurePartner.apiKey)} title="Copy" aria-label="Copy"><CopyIcon /></button>
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <th>Corridor</th>
                          <td>
                            <CorridorWithFlags from={configurePartner.corridorFrom} to={configurePartner.corridorTo} status={configurePartner.status} />
                          </td>
                        </tr>
                        <tr>
                          <th>Date approved</th>
                          <td>{formatDateTime(configurePartner.dateApproved)}</td>
                        </tr>
                        <tr>
                          <th>Date activated</th>
                          <td>{formatDateTime(configurePartner.dateActivated) || '—'}</td>
                        </tr>
                      </tbody>
                    </table>
                    {showDelinkConfirm && (
                      <div className="delink-danger">
                        <p><strong>Are you sure you want to delink from this partner app?</strong> This will revoke access and change status back to Approved.</p>
                        <div className="delink-actions">
                          <button type="button" className="btn btn-secondary" onClick={() => setShowDelinkConfirm(false)}>Cancel</button>
                          <button type="button" className="btn btn-danger" onClick={handleDelink}>Delink</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {configurePartner.status === 'Active' && (
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => setShowDelinkConfirm(true)}
                    disabled={showDelinkConfirm}
                  >
                    Delink
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Credentials popup (Client ID, Client Secret, App ID, API Key) */}
        {showCredentialsPopup && (
          <div className="modal-overlay" onClick={() => setShowCredentialsPopup(false)}>
            <div className="modal-content credentials-popup" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header modal-header-blue">
                <h3>Save credentials</h3>
                <button type="button" className="modal-close modal-close-white" onClick={() => setShowCredentialsPopup(false)} aria-label="Close">×</button>
              </div>
              <div className="modal-body">
                <p className="popup-desc">Enter OAuth 2.0 and API credentials. These will be stored securely.</p>
                <div className="credentials-form">
                <div className="form-group">
                  <label>Client ID</label>
                  <input
                    type="text"
                    className="form-input"
                    value={credentials.clientId}
                    onChange={(e) => setCredentials((c) => ({ ...c, clientId: e.target.value }))}
                    placeholder="OAuth client ID"
                  />
                </div>
                <div className="form-group">
                  <label>Client Secret</label>
                  <input
                    type="password"
                    className="form-input"
                    value={credentials.clientSecret}
                    onChange={(e) => setCredentials((c) => ({ ...c, clientSecret: e.target.value }))}
                    placeholder="OAuth client secret"
                  />
                </div>
                <div className="form-group">
                  <label>App ID</label>
                  <input
                    type="text"
                    className="form-input"
                    value={credentials.appId}
                    onChange={(e) => setCredentials((c) => ({ ...c, appId: e.target.value }))}
                    placeholder="Application ID"
                  />
                </div>
                <div className="form-group">
                  <label>API Key</label>
                  <input
                    type="text"
                    className="form-input"
                    value={credentials.apiKey}
                    onChange={(e) => setCredentials((c) => ({ ...c, apiKey: e.target.value }))}
                    placeholder="API key"
                  />
                </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCredentialsPopup(false)}>Cancel</button>
                <button type="button" className="btn btn-add-partner" onClick={saveCredentials}>Save</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
};

export default Partners;
